import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  Tooltip,
} from '@mui/material';
import { ChevronUp, ChevronDown } from 'lucide-react';

const ACCENT = '#2CB8A0';
const HOVER_BG = 'rgba(26, 58, 92, 0.04)';
const ACTIVE_BG = 'rgba(26, 58, 92, 0.06)';

export default function SidebarSection({
  id,
  label,
  icon: Icon,
  path,
  open,
  active,
  onClick,
  items = [],
  children = null,
  mini = false,
  depth = 0,
  itemPl = 4.5,
}) {
  const location = useLocation();
  const hasExpandable = items.length > 0 || React.Children.count(children) > 0;

  const header = (
    <ListItemButton
      selected={active}
      onClick={onClick}
      sx={{
        minHeight: 44,
        width: '100%',
        pl: mini ? 1.5 : 2,
        pr: mini ? 1.5 : 2,
        justifyContent: mini ? 'center' : 'flex-start',
        borderLeft: active ? `3px solid ${ACCENT}` : '3px solid transparent',
        bgcolor: active ? ACTIVE_BG : 'transparent',
        color: active ? ACCENT : 'text.primary',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
        '&:hover': { bgcolor: HOVER_BG },
        '&.Mui-selected:hover': { bgcolor: ACTIVE_BG },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: mini ? 0 : 40,
          justifyContent: 'center',
          color: active ? ACCENT : 'text.secondary',
        }}
      >
        <Icon size={mini ? 22 : 20} />
      </ListItemIcon>

      {!mini && (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.93rem',
              fontWeight: active ? 700 : 500,
              noWrap: true,
            }}
          />
          {hasExpandable && (
            <ListItemIcon
              sx={{
                minWidth: 0,
                color: 'text.secondary',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </ListItemIcon>
          )}
        </>
      )}
    </ListItemButton>
  );

  return (
    <ListItem component="div" disablePadding sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      {mini ? (
        <Tooltip title={label} placement="right" arrow>
          {header}
        </Tooltip>
      ) : (
        header
      )}

      {!mini && hasExpandable && (
        <Collapse in={open} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
          <List disablePadding sx={{ width: '100%' }}>
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              const ItemIcon = item.icon;
              return (
                <ListItem
                  key={item.text}
                  disablePadding
                  component={Link}
                  to={item.path}
                  sx={{ color: 'inherit', textDecoration: 'none', width: '100%' }}
                >
                  <ListItemButton
                    selected={isActive}
                    sx={{
                      width: '100%',
                      pl: itemPl,
                      pr: 2,
                      minHeight: 38,
                      borderLeft: isActive ? `3px solid ${ACCENT}` : '3px solid transparent',
                      bgcolor: isActive ? ACTIVE_BG : 'transparent',
                      color: isActive ? ACCENT : 'text.primary',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                      '&:hover': { bgcolor: HOVER_BG },
                      '&.Mui-selected:hover': { bgcolor: ACTIVE_BG },
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 40, color: isActive ? ACCENT : 'text.secondary' }}
                    >
                      <ItemIcon size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.84rem',
                        fontWeight: isActive ? 700 : 400,
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {children}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
}
